// =========================================================================
// Local verification — everything this page decides for itself.
//
// The server supplies the transaction. It does NOT get to tell us what the
// transaction means. Three independent checks run here, in the browser:
//
//   1. DECODE THE SIGNED BYTES. Not the JSON beside them — the protobuf that
//      the signature actually covers. This is the fix for the flaw an
//      adversarial review found: a server can serve benign JSON alongside
//      hostile bytes, and the operator signs the bytes.
//
//   2. RECOMPUTE THE txID. sha256 over those same bytes. This is the string
//      the Ledger hashes, so it is the one value that ties what we show to
//      what the device signs.
//
//   3. ANCHOR THE RECIPIENT to what the operator confirmed ON THIS DEVICE. A
//      server that supplies both the transaction and the "expected" values
//      only proves it agrees with itself. So the device keeps its own short
//      list — one entry per destination the operator has confirmed by typing
//      part of its address from a source that is not this page — and a
//      destination that is not on it is not signed until he does.
//
// What none of this can do is make the page itself trustworthy. That is what
// the Ledger screen is for, and the UI says so.
// =========================================================================

import { decodeSignedBytes, USDT_BY_NETWORK } from '../../signer/src/tron-tx.mjs';
// The longest a transaction may stay signable, by this device's clock: assertCosignSafe's bound (B10), for the dashboard's sheet.
export { MAX_COSIGN_WINDOW_MS } from '../../signer/src/tron-tx.mjs';
import { decodeTronAddress } from '../../signer/src/address.mjs';

export interface Decoded {
  txID: string;
  owner: string;
  contract: string;
  to: string;
  amount: bigint;
  feeLimit: bigint;
  permissionId: number;
  expiration: number | null;
}

/**
 * 'party' — a counterparty payout address, approved on a Ledger before it is payable.
 * 'desk'  — one of our own wallets: a sweep, refill, vault or quarantine destination.
 *
 * Since 18 Oct 2026 the distinction IS the trust decision (anchorRecipient): a
 * counterparty's address is typed once per device from the counterparty's own
 * record; one of the desk's own wallets is not typed — the Ledger's screen is
 * its check — and the device only keeps, per desk layer, the address it last
 * signed to.
 */
export type EntryKind = 'party' | 'desk';

/** The desk's words for a card's kind — never the code on screen (the operator's word rule, 16 Oct 2026). */
export const KIND_WORDS: Readonly<Record<string, string>> = Object.freeze({
  sweep: 'Sweep',
  payout: 'Payout',
  refill: 'Refill',
  vault_move: 'Treasury move',
  quarantine_move: 'Flagged move',
  renewal_move: 'Renewal move',
  release: 'Release',
});

/** What the server says about this card's destination. One entry — never a list. */
export interface DestinationEntry {
  address: string;
  partyName: string;
  approvedAt: string | null;
  kind: EntryKind;
  /** The desk layer of one of our own wallets; null for a counterparty. */
  layer: string | null;
  /**
   * For one of our own wallets issued from a registered key: the Ledger
   * account that key is, and the register's path under it ("0/7"). The device
   * derives the address there when it signs and refuses the card if the bytes
   * pay anything else (audit of 21 Oct 2026, finding 1). Null for a wallet
   * registered by hand: nothing on the device derives it.
   */
  account: number | null;
  derivation: string | null;
}

/** A destination the operator confirmed on this device, and what it was called when he did. */
export interface ConfirmedDestination {
  address: string;
  partyName: string;
  kind: EntryKind;
  /**
   * For one of the desk's own wallets: its register layer (one of
   * SIGNABLE_DESK_LAYERS). The record is KEYED on this, never on the name —
   * a name is a string the server chooses, and one invisible character in it
   * would make a swapped wallet read as a different one. Absent on records
   * written before 18 Oct 2026, which then neither raise nor suppress a notice.
   */
  layer?: string;
  confirmedAt: string;
}

export type Anchor =
  | { state: 'known'; partyName: string; kind: EntryKind }
  /**
   * One of the desk's own wallets, issued from a key this Ledger holds. Not
   * typed and not a banner: the device derives the address at this account
   * and path before it signs, and the transfer is not sent to it unless the
   * two are equal (ledger.ts signOnLedger, `prove`). Until 21 Oct 2026 the
   * server's label was enough here, and a renewal's amber line was the only
   * notice — routine after the first renewal (audit of 21 Oct, finding 1).
   */
  | { state: 'desk'; partyName: string; kind: 'desk'; layer: string; account: number; derivation: string }
  | {
      state: 'unconfirmed'; partyName: string; kind: EntryKind;
      /** Another address this device already holds under the same name, if there is one. */
      heldElsewhere: string | null;
    }
  /** account and derivation: a desk wallet the device still proves once the ending is typed (deviceProof). */
  | { state: 'changed'; partyName: string; kind: EntryKind; detail: string; account?: number; derivation?: string }
  | { state: 'unapproved'; partyName: string; kind: EntryKind }
  | { state: 'unpayable'; partyName: string }
  | { state: 'unknown' };

/** Decode the bytes the signature covers. Throws with a displayable reason. */
export function decodeForDisplay(rawDataHex: string): Decoded {
  return decodeSignedBytes(rawDataHex) as Decoded;
}

// ---- which network this build signs for ----------------------------------

export type Network = 'mainnet' | 'nile';

/**
 * The TRON network a build of this page signs for, from the environment it was
 * built with. Two builds sign anything: production (mainnet) and nile. Any
 * other value names no network, and the page signs nothing.
 *
 * The token is pinned to this, not to "a USDT contract this page has heard
 * of": the page used to accept the Nile test token on a mainnet build, where
 * that address is not USDT at all (review of 4 October, R2).
 */
export function networkOf(environment: string): Network | null {
  if (environment === 'production') return 'mainnet';
  if (environment === 'nile') return 'nile';
  return null;
}

/** The one token contract this page signs transfers of on a network — never one the server names. */
export function usdtContract(network: Network): string {
  return USDT_BY_NETWORK[network];
}

// ---- the destinations confirmed on this device ---------------------------
//
// This list is the one thing here a compromised server cannot rewrite, and it
// only stays that way if the server never writes it. It used to: the page saved
// the server's whole address list at every clean load, before any tap, so an
// address inserted into the database became "known" the next time the operator
// opened any other card, and a later card paying it showed green (review of 4
// October, R3). And a device with no list at all signed on a yellow banner
// (R5).
//
// Now the only writer is confirmDestination(), and main.ts calls it in one
// place: after the operator typed the end of the address, approved the
// transfer on his device, and the server accepted that signature as coming
// from his registered device. One destination at a time, never the server's
// list.

/**
 * Where this device keeps its list — one per network, so that an address
 * confirmed on the test network does not read as known to a live build served
 * from the same place.
 */
export function confirmedKey(network: Network): string {
  return `dubaiotc.cosign.confirmed.v2.${network}`;
}

/**
 * Where the adopted-from-the-server list lived. Nothing in it was confirmed by
 * anyone, so none of it is carried over — and it held every payout address and
 * every client's deposit address, which has no business staying on a phone.
 */
export const LEGACY_SNAPSHOT_KEY = 'dubaiotc.whitelist.snapshot.v1';

/** How many characters, from the end of an address, the operator types to confirm it. */
export const CONFIRM_TAIL = 6;

function isConfirmed(e: unknown): e is ConfirmedDestination {
  const c = e as Partial<ConfirmedDestination> | null;
  return !!c && typeof c.address === 'string' && typeof c.partyName === 'string'
    && (c.kind === 'party' || c.kind === 'desk') && typeof c.confirmedAt === 'string'
    && (c.layer === undefined || typeof c.layer === 'string');
}

/** The destinations confirmed on this device. Empty when there are none, or when storage cannot be read. */
export function loadConfirmed(network: Network): ConfirmedDestination[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(confirmedKey(network)) ?? '[]');
    // Kept as confirmed, compared as kept; drawn through shownAs where the page draws it.
    return Array.isArray(parsed) ? parsed.filter(isConfirmed) : [];
  } catch {
    return [];
  }
}

/**
 * Record that the operator confirmed this one destination, under the name it
 * was shown with. Replaces an earlier confirmation of the same address.
 *
 * @returns false when this browser would not keep it (private mode, quota) —
 *          the page then asks again next time, and says so.
 */
export function confirmDestination(
  network: Network,
  entry: { address: string; partyName: string; kind: EntryKind; layer?: string | null },
  now: Date = new Date(),
): boolean {
  try {
    const layer = entry.kind === 'desk' && typeof entry.layer === 'string' && entry.layer ? entry.layer : undefined;
    // One record per address — and, for the desk's own wallets, one per
    // LAYER: Hot has one current address (a hand-registered desk row is typed
    // and recorded like a counterparty's; a key-issued one is proven on the
    // device and recorded by nothing).
    const kept = loadConfirmed(network).filter((e) =>
      e.address !== entry.address
      && !(layer !== undefined && e.kind === 'desk' && e.layer === layer));
    kept.push({
      address: entry.address, partyName: entry.partyName, kind: entry.kind,
      ...(layer !== undefined ? { layer } : {}),
      confirmedAt: now.toISOString(),
    });
    localStorage.setItem(confirmedKey(network), JSON.stringify(kept));
    return true;
  } catch {
    return false;
  }
}

/** Drop the old adopted list from this device. */
export function forgetLegacySnapshot(): void {
  try { localStorage.removeItem(LEGACY_SNAPSHOT_KEY); } catch { /* nothing to drop */ }
}

/** An address with the part the operator must type hidden. */
export function maskTail(address: string): string {
  return `${address.slice(0, -CONFIRM_TAIL)}${'•'.repeat(CONFIRM_TAIL)}`;
}

/**
 * Whether these texts, read off a screen, give away the ending of an address.
 *
 * The typing gate hides the ending. A server that wanted it passed from this
 * screen would put it somewhere else on the page: in a name it chose, in the
 * amount, in a transaction id it ground for the purpose. So everything that is
 * shown beside the gate is searched, with compatibility letterforms folded and
 * everything but letters and digits removed (spacing, punctuation, invisible
 * characters), forwards and backwards (text can be stored reversed behind a
 * direction mark).
 *
 * A run that carries on from one text into another is found WHATEVER the order
 * the texts are drawn in (Screens and Astra on the signing restyle, 27 Oct
 * 2026: the check joined the texts in one fixed order, so an ending split
 * between the source's name and the payee's — neighbours on the card in one
 * order or the other — was never contiguous in the scan, and the gate was
 * offered). The ending counts as shown when it is the end of one text, then
 * any number of whole other texts, then the start of another — any texts, in
 * any order, each used once.
 *
 * And it counts as shown when it is cut in TWO anywhere (Screens and Astra,
 * 27 Oct 2026, round 2): one text holds its first part and a text — the same
 * one too — holds the rest, wherever in them, in either order. The page draws
 * a name in several places (the To row, the anchor, the gate's lines), so
 * "fRs · Gulf Bridge FZE · cff" puts the end beside the start on screen; and
 * pieces in the middle of two names are read together by an eye, not a scan.
 * A false refusal needs both halves of a 6-character base58 ending in the
 * page's own words: about 58^-4 a page.
 *
 * Direction controls are refused before any of this (carriesDirectionControl):
 * a reversal inside one name DISPLAYS an order the scan cannot see.
 *
 * Letters are compared as a person READS them (Screens, 27 Oct 2026, round 3):
 * the strip used to DROP a Cyrillic or Greek look-alike ("сfffRs" with a
 * Cyrillic с) or an accented letter ("ćfffRs"), leaving "fffRs" while the eye
 * read the whole ending. Each text is reduced to its skeleton first — every
 * accent removed (NFKD, combining marks dropped), each look-alike mapped to
 * the Latin letter it imitates — and only then stripped.
 *
 * This catches the ending SHOWN. It cannot catch it DESCRIBED — "the same six
 * as the address in my message" — nor cut into three pieces each in the MIDDLE
 * of three texts ("Xcf Y", "Pff Q", "MRs N"): no scan of a page closes that.
 * What stands against those is the operator's own record and the device's
 * screen.
 */
export function showsEnding(texts: string[], address: string): boolean {
  // The page passes a dozen texts; anything far beyond that is not a card this page draws (and the run search grows
  // with the number of texts) — refused as shown rather than searched.
  if (texts.length > MAX_SHOWN_TEXTS) return true;
  const tail = address.slice(-CONFIRM_TAIL);
  const bare = texts.map(skeleton).filter((t) => t.length > 0);
  return [tail, [...tail].reverse().join('')].some((run) => runAcross(run, bare) || cutInTwo(run, bare));
}

// The rendered card's text nodes, not a hand-kept list (round 3): a few dozen; far beyond is refused unsearched.
const MAX_SHOWN_TEXTS = 96;
const RUN_STEPS = 20_000;

/**
 * Cyrillic and Greek letters that are read as Latin ones (Unicode's confusables, the letters that matter for a base58
 * ending). Not every confusable in Unicode: the ones a server can type into a name to show a Latin letter.
 */
const LOOKALIKE: Readonly<Record<string, string>> = Object.freeze({
  // Cyrillic
  'а': 'a', 'в': 'B', 'е': 'e', 'к': 'k', 'м': 'M', 'н': 'H', 'о': 'o', 'р': 'p', 'с': 'c', 'т': 'T', 'у': 'y', 'х': 'x',
  'і': 'i', 'ј': 'j', 'ѕ': 's', 'ԁ': 'd', 'һ': 'h', 'ӏ': 'l', 'ԛ': 'q', 'ԝ': 'w', 'ь': 'b', 'ɡ': 'g',
  'А': 'A', 'В': 'B', 'Е': 'E', 'К': 'K', 'М': 'M', 'Н': 'H', 'О': 'O', 'Р': 'P', 'С': 'C', 'Т': 'T', 'У': 'Y', 'Х': 'X',
  'І': 'I', 'Ј': 'J', 'Ѕ': 'S', 'Ԛ': 'Q', 'Ԝ': 'W', 'Ү': 'Y', 'Һ': 'h', 'Ӏ': 'l',
  // Greek
  'α': 'a', 'ο': 'o', 'ν': 'v', 'ρ': 'p', 'τ': 't', 'υ': 'u', 'χ': 'x', 'ι': 'i', 'κ': 'k', 'γ': 'y',
  'Α': 'A', 'Β': 'B', 'Ε': 'E', 'Ζ': 'Z', 'Η': 'H', 'Ι': 'I', 'Κ': 'K', 'Μ': 'M', 'Ν': 'N', 'Ο': 'O', 'Ρ': 'P', 'Τ': 'T',
  'Υ': 'Y', 'Χ': 'X',
});

/** A text as it is READ: compatibility forms folded, accents dropped, look-alikes mapped, then letters and digits only. */
export function skeleton(text: string): string {
  return [...text.normalize('NFKD').replace(/\p{M}/gu, '')].map((c) => LOOKALIKE[c] ?? c).join('').replace(/[^A-Za-z0-9]/g, '');
}

/**
 * Whether a text the server chose mixes Latin letters with Cyrillic or Greek ones — the form a disguised ending takes
 * ("Northwind сfffRs"). A second layer under the skeleton: real names do not mix those scripts in one name.
 */
export function mixesLookalikeScripts(texts: readonly string[]): boolean {
  return texts.some((t) => /\p{Script=Latin}/u.test(t) && /[\p{Script=Cyrillic}\p{Script=Greek}]/u.test(t));
}

/** Whether some text holds the run's first k characters and some text (the same allowed) the rest — anywhere in them. */
function cutInTwo(run: string, texts: readonly string[]): boolean {
  for (let k = 1; k < run.length; k++) {
    const head = run.slice(0, k), rest = run.slice(k);
    if (texts.some((t) => t.includes(head)) && texts.some((t) => t.includes(rest))) return true;
  }
  return false;
}

/**
 * The explicit direction controls (embeddings, overrides, isolates, and the left/right marks): a text the server
 * controls that carries one can DISPLAY characters in an order no scan of its code points sees — "cff‮sRf‬" shows
 * cfffRs (Screens and Astra, 27 Oct 2026). Real names do not carry them; such a card is refused before the gate.
 */
// Every bidi control Unicode names (Bidi_Control: the embeddings, overrides and isolates, LRM/RLM and the ARABIC LETTER
// MARK U+061C — "؜24؜92؜19" DISPLAYS 199224, Screens' round 3).
const DIRECTION_CONTROL = /\p{Bidi_Control}/u;
export function carriesDirectionControl(texts: readonly string[]): boolean {
  return texts.some((t) => DIRECTION_CONTROL.test(t));
}

/** The From line as the Ledger's TRON app shows it: "P<n> - <owner>" for a non-default permission, the bare owner for 0. */
export function fromAsDevice(permissionId: number, owner: string): string {
  return permissionId > 0 ? `P${permissionId} - ${owner}` : owner;
}

/**
 * The one line always on screen above Sign: what to compare on the device, by network — within the platform's twelve
 * words (Screens, 27 Oct 2026). On Nile the device cannot show test USDT; the why is behind "What to compare".
 */
export function nanoLine(network: Network): string {
  return network === 'nile'
    ? 'On the Nano: Contract and From. Reject any difference.'
    : 'On the Nano: To, Amount, From, Max Fee. Reject any difference.';
}

/** Whether `run` is inside one text, or is a text's end, whole other texts, then a text's start — any order, each once. */
function runAcross(run: string, texts: readonly string[]): boolean {
  if (texts.some((t) => t.includes(run))) return true;
  // What is left of the run after the texts already used: whole texts, then the start of one more. The search is
  // BOUNDED, and past its bound it answers "shown" (Screens on 59cbd67d: equal one-letter texts made it exponential —
  // 60 took 236 s); the page's own texts are distinct and take a few hundred steps.
  let steps = 0;
  const rest = (left: string, used: ReadonlySet<number>): boolean => ++steps > RUN_STEPS || texts.some((t, j) => !used.has(j) && (
    t.startsWith(left) || (t.length < left.length && left.startsWith(t) && rest(left.slice(t.length), new Set([...used, j])))
  ));
  return texts.some((t, i) => {
    for (let k = 1; k < run.length; k++) {
      if (t.endsWith(run.slice(0, k)) && rest(run.slice(k), new Set([i]))) return true;
    }
    return false;
  });
}

// ---- the anchor ----------------------------------------------------------

/**
 * The desk layers a signed transfer may PAY. The server applies the same list
 * (bff/src/db/wallets.ts SIGNABLE_DESTINATION_LAYERS); this copy is fixed in
 * the page's build so that a server-side slip — a deposit address or the FEE
 * wallet offered as a destination — is refused here rather than shown as
 * "your own". Not L0: money is swept out of a deposit address, never into it.
 * Not FEE: that is the one wallet whose key is on the server.
 *
 * It stops a SLIP. A server that lies about the layer is not stopped by it:
 * what stands there is the typing gate and the device's own list.
 */
// The register's classes (main's model, 16 Oct 2026): the floats, frozen,
// treasury. Not 'deposit' and not 'fee'. The page keeps the field name
// `layer` on the wire; the words are classes.
export const SIGNABLE_DESK_LAYERS: readonly string[] = ['running_hot', 'dirty_hot', 'frozen', 'treasury'];

/** The server's entry for the destination, if it is one this page can use. */
export function parseDestination(raw: unknown): DestinationEntry | null {
  const e = raw as Partial<DestinationEntry> | null;
  if (!e || typeof e !== 'object') return null;
  if (typeof e.address !== 'string' || typeof e.partyName !== 'string' || !e.partyName) return null;
  if (e.kind !== 'party' && e.kind !== 'desk') return null;
  return {
    address: e.address,
    // As sent: compared and kept in this form. The page DRAWS it through shownAs (main.ts `shown`) — sanitised here,
    // two names that draw alike compared equal and a renamed payee read as known (Screens on 59cbd67d).
    partyName: e.partyName,
    approvedAt: typeof e.approvedAt === 'string' && e.approvedAt ? e.approvedAt : null,
    kind: e.kind,
    layer: typeof e.layer === 'string' ? e.layer : null,
    account: typeof e.account === 'number' && Number.isInteger(e.account) && e.account >= 0 ? e.account : null,
    derivation: typeof e.derivation === 'string' && e.derivation.trim() ? e.derivation.trim() : null,
  };
}

/**
 * The desk layers each card kind may pay. A payout pays a counterparty, never
 * a layer. The server's two labels — the card's kind and its destination —
 * must agree, or one of them lies: a "sweep" paying Treasury, a "vault move"
 * paying a counterparty (audit of 21 Oct 2026, finding 1).
 */
/**
 * Which Ledger account each renewable layer's addresses are issued from —
 * the contract's KEY_ACCOUNT_CLASS (contracts/wallet.mjs), pinned here
 * because this page carries no imports it cannot audit by eye. A desk entry
 * naming a layer and an account that do not belong together is a forged
 * entry: a Hot address presented as Treasury (Astra, 22 Oct 2026).
 */
export const ACCOUNT_LAYERS: Readonly<Record<number, string>> = Object.freeze({ 0: 'deposit', 1: 'running_hot', 2: 'dirty_hot', 3: 'treasury' });

/** A layer's word on this page: the desk's own words (contracts CLASS_WORDS), pinned. */
export function layerWord(layer: string): string {
  return ({ deposit: 'a Deposit address', running_hot: 'Hot', dirty_hot: 'Merchant Hot', frozen: 'Flagged', treasury: 'Treasury', fee: 'Fee' } as Record<string, string>)[layer] ?? layer;
}

export const KIND_LAYERS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  sweep: ['running_hot', 'dirty_hot'],
  refill: ['running_hot', 'dirty_hot'],
  vault_move: ['treasury'],
  quarantine_move: ['frozen'],
  renewal_move: ['running_hot', 'dirty_hot', 'treasury'],
  // Release (db/0105, N-099): one deposit's USDT from Merchant Hot to Hot — Hot only.
  release: ['running_hot'],
  payout: [],
});

/** Why the card's kind and its destination cannot both be true — or null when they agree (or there is no entry: the anchor refuses that on its own). */
export function kindMismatch(kind: unknown, entry: DestinationEntry | null): string | null {
  if (!entry) return null;
  const word = KIND_WORDS[String(kind)] ?? String(kind);
  const layers = KIND_LAYERS[String(kind)];
  // The server's texts in it as the page draws them (shownAs).
  if (!layers) return `Mismatch — do not sign: no card is called "${shownAs(String(kind))}".`;
  if (entry.kind === 'party') {
    return layers.length === 0 ? null : `Mismatch — do not sign: the card is called ${word} but pays a counterparty, ${shownAs(entry.partyName)}.`;
  }
  const layer = entry.layer ?? '';
  return layers.includes(layer) ? null : `Mismatch — do not sign: the card is called ${word} but pays ${shownAs(entry.partyName)}${layer ? ` (${shownAs(layerWord(layer))})` : ''}.`;
}

/** What the device must derive before it signs: the desk destination's account and path, and the address the bytes pay. Null when nothing is to be proven. */
export function deviceProof(a: Anchor, to: string): { account: number; derivation: string; expect: string; name: string } | null {
  // The name as the page draws it (shownAs): the proof's messages are written after the paint (round 7).
  // A 'changed' desk wallet typed past is proven too (Astra on the whole page, 3 Nov 2026: typing it dropped the proof).
  return 'derivation' in a && a.derivation !== undefined ? { account: a.account!, derivation: a.derivation, expect: to, name: shownAs(a.partyName) } : null;
}

/**
 * The key the desk takes a signature from for this card, as the card names it.
 *
 *   { address }  the device must sign with the key of this address
 *   null         no key the operator holds can sign it: the money is in a wallet
 *                that only its own key moves, and that key is not his registered
 *                Ledger's (review of 4 October, R1)
 *   'unreadable' anything else — the page and the server out of step
 */
export function parseSigner(raw: unknown): { address: string } | null | 'unreadable' {
  if (raw === null) return null;
  const s = raw as { address?: unknown } | undefined;
  if (!s || typeof s !== 'object' || typeof s.address !== 'string') return 'unreadable';
  try {
    decodeTronAddress(s.address);
  } catch {
    return 'unreadable';
  }
  return { address: s.address };
}

/**
 * Place the recipient: what the desk says it is, and whether the operator has
 * confirmed it on this device.
 *
 * @param to         the recipient decoded from the signed bytes
 * @param entry      the server's entry for this card's destination, or null
 * @param confirmed  loadConfirmed()
 */
export function anchorRecipient(
  to: string,
  entry: DestinationEntry | null,
  confirmed: ConfirmedDestination[],
  /** Whether the page can have the Ledger derive a desk address before it signs (a cable). Without one, a desk wallet is typed like a counterparty's. */
  canProve = true,
): Anchor {
  // 1. REGISTERED AT ALL?
  //
  // Checked first, and deliberately so: whether the desk knows this address is
  // a separate question from whether this device has seen it, and no history on
  // the device makes an unregistered address acceptable. An entry for some
  // OTHER address is no entry for this one.
  if (!entry || entry.address !== to) return { state: 'unknown' };

  // 2. ONE OF OURS THAT NOTHING SIGNED SHOULD PAY?
  if (entry.kind === 'desk' && !SIGNABLE_DESK_LAYERS.includes(entry.layer ?? '')) {
    return { state: 'unpayable', partyName: entry.partyName };
  }

  // 3. APPROVED ON A DEVICE?
  //
  // The server's word, so it is necessary and never sufficient: step 4 is the
  // part the server cannot answer for. Desk-owned wallets carry their creation
  // time in this field — they come from the key ceremony, not from an
  // address-book proposal.
  if (!entry.approvedAt) {
    return { state: 'unapproved', partyName: entry.partyName, kind: entry.kind };
  }

  // 4. CONFIRMED ON THIS DEVICE, UNDER THIS NAME?
  //
  // Two regimes (the operator, 18 Oct 2026, on being asked to type the end of
  // his own Hot wallet's address: "too clumsy and complex").
  //
  // THE DESK'S OWN WALLETS are not typed. They come from the register and the
  // key ceremony, and a move between two of them is checked on the Ledger's
  // screen, which shows the address the bytes pay. What this device keeps for
  // them is a silent record of the address it last signed to under each name
  // (written after an accepted signature, below), so a NEW address under an
  // old name — right after a renewal, and what a swapped wallet looks like —
  // is said out loud without stopping the ceremony. A desk name on an address
  // this device confirmed as a COUNTERPARTY's is still 'changed': that is a
  // customer's address being passed off as ours.
  const mine = confirmed.find((e) => e.address === to);
  const layer = entry.layer ?? '';
  // One of ours this device once confirmed under ANOTHER layer is said, in both regimes (Astra on the whole page, 3 Nov
  // 2026: a Hot confirmation saved through TronLink passed a card calling the same address Treasury).
  const otherLayer = mine?.kind === 'desk' && typeof mine.layer === 'string' && mine.layer && mine.layer !== layer
    ? `on this device you confirmed this address as ${layerWord(mine.layer)}, not ${layerWord(layer)}` : null;
  if (entry.kind === 'desk' && entry.account !== null && entry.derivation !== null && canProve) {
    // Issued from a registered key: the device itself derives the address at
    // this account and path when it signs, and refuses the card if the bytes
    // pay anything else. No record on this device is consulted — a stored
    // address proves nothing the device cannot, and a server that can write
    // the destination can write the record's name too. One of ours that this
    // device confirmed as a COUNTERPARTY's is still 'changed': that is a
    // customer's address being passed off as the desk's.
    // The account must be the layer's own: a proof at account 1 proves a Hot
    // address, never a Treasury one, whatever the entry says its layer is. An
    // entry that contradicts itself is not typed past — typing it used to drop
    // the proof as well (Astra on the whole page, 3 Nov 2026): it is reported.
    if (ACCOUNT_LAYERS[entry.account] !== layer) return { state: 'unpayable', partyName: entry.partyName };
    // The path is held inside that account at the device (ledger.ts destinationPath: change/index only — the pre-live
    // red team and Astra, 29 Oct 2026: "0'/0/7" under account 3 proved a Hot-account address as Treasury). Refused
    // there, at the tap, before anything is signed; not here too — the signing page's bundle has no room for both.
    // Typed past, a 'changed' desk wallet is still proven on the device at its account and path (deviceProof).
    const at = { account: entry.account, derivation: entry.derivation };
    if (mine && mine.kind !== 'desk') {
      return {
        state: 'changed', partyName: entry.partyName, kind: entry.kind, ...at,
        detail: 'on this device you confirmed this address as a counterparty\'s, not the desk\'s own',
      };
    }
    if (otherLayer) return { state: 'changed', partyName: entry.partyName, kind: entry.kind, detail: otherLayer, ...at };
    return { state: 'desk', partyName: entry.partyName, kind: 'desk', layer, ...at };
  }
  // One of ours registered BY HAND, with no key the device could derive it
  // from (the first Flagged and Treasury rows) — or any desk wallet on a page
  // with no cable to prove it on: nothing on the device can vouch for it, so
  // it is typed once per device like a counterparty's — the one check a
  // compromised server cannot pass for the operator.

  // A COUNTERPARTY'S ADDRESS is typed once per device, from the counterparty's
  // own confirmation of it: the one record a compromised server cannot write.
  if (!mine) {
    // A name this device already holds for ANOTHER address is worth saying out
    // loud: it is right when a wallet was replaced, and it is also what a
    // different wallet presented under a familiar name looks like.
    const namesake = confirmed.find((e) => e.partyName === entry.partyName);
    return {
      state: 'unconfirmed', partyName: entry.partyName, kind: entry.kind,
      heldElsewhere: namesake ? namesake.address : null,
    };
  }
  // The KIND first: a counterparty's address passed off as the desk's own (or
  // the reverse) is the graver lie, whatever name it comes under.
  if (mine.kind !== entry.kind) {
    return {
      state: 'changed', partyName: entry.partyName, kind: entry.kind,
      detail: mine.kind === 'desk'
        ? 'on this device you confirmed this address as one of the desk\'s own wallets'
        : 'on this device you confirmed this address as a counterparty\'s, not the desk\'s own',
    };
  }
  if (otherLayer) return { state: 'changed', partyName: entry.partyName, kind: entry.kind, detail: otherLayer };
  if (mine.partyName !== entry.partyName) {
    return {
      state: 'changed', partyName: entry.partyName, kind: entry.kind,
      detail: `on this device you confirmed this address as "${mine.partyName}"`,   // drawn through shownAs (main.ts `shown`)
    };
  }
  return { state: 'known', partyName: entry.partyName, kind: entry.kind };
}

/**
 * Which anchor states must stop the ceremony: every one except a destination
 * that is registered, approved, and confirmed on this device under the name it
 * has now. A red banner over a working Sign button teaches operators to click
 * past red banners, so these disable the button structurally.
 *
 *   unknown      not registered with the desk at all
 *   unpayable    one of our own wallets that nothing signed should pay
 *   unapproved   registered, but never approved on a Ledger
 *   unconfirmed  a counterparty's address never confirmed on this device — the
 *                first time, on every device
 *   changed      confirmed here under another name, or the other kind
 *
 * 'desk' — one of the desk's own wallets issued from a registered key — does
 * not stop it here: the Ledger derives the address at the card's account and
 * path before it signs, and refuses the card if the bytes pay anything else.
 */
export function anchorBlocksSigning(a: Anchor): boolean {
  return a.state !== 'known' && a.state !== 'desk';
}

/**
 * Whether the operator can type his way past this state.
 *
 * 'unconfirmed' and 'changed' only. Typing the end of an address he read from
 * the counterparty, or from the desk's own record of its wallets, IS the
 * confirmation those two states are waiting for.
 *
 * The other three have no path. Typing an address proves he read it; it says
 * nothing about whether the desk registered it, whether a Ledger ever approved
 * it, or whether it is a wallet that should be paid at all — so offering the
 * same gate there would be theatre. The remedy is to register or approve the
 * entry, or to report the card.
 */
export function anchorIsOverridable(a: Anchor): boolean {
  return a.state === 'unconfirmed' || a.state === 'changed';
}

/** Cross-check the server's claimed summary against what we decoded ourselves. */
export function crossCheck(
  decoded: Decoded,
  claimed: { to?: string; amount?: string; from?: string },
  /**
   * How a recipient address is written into a message. The page passes
   * maskTail for a destination the device does not know, so that a refusal
   * never shows the ending the operator may later be asked to type — neither
   * the real recipient's nor whatever the server claimed in its place.
   */
  showRecipient: (address: string) => string = (address) => address,
): string[] {
  // The card's own claims are never drawn, only the bytes' values: a claim is the server's text, and a refusal that
  // printed it drew whatever the server wrote there (Astra on the whole page, 3 Nov 2026).
  const problems: string[] = [];
  if (claimed.to && claimed.to !== decoded.to) {
    problems.push(`the card names another recipient; the signed bytes pay ${showRecipient(decoded.to)}`);
  }
  if (claimed.from && claimed.from !== decoded.owner) {
    // A card that names one wallet while the bytes spend from another would
    // show the wrong name beside "From" (review of 4 October, R24).
    problems.push(`the card names another wallet the money leaves; the signed bytes spend from ${decoded.owner}`);
  }
  if (claimed.amount !== undefined) {
    // claimed.amount is a decimal string; compare in base units.
    const claimedUnits = decimalToUnits(claimed.amount, 6);
    if (claimedUnits === null) {
      // A claim we cannot parse is a claim we cannot check — and an unchecked
      // claim must never read as an agreeing one. Skipping here is how the
      // whole cross-check switches itself off silently: Postgres numeric(38,8)
      // renders as '40.00000000', which has more than six decimal places, and
      // the page would have gone on displaying "decoded in this browser" while
      // comparing nothing at all.
      problems.push(`the card's amount is not a value this page can compare; the signed bytes say ${unitsToDecimal(decoded.amount, 6)}`);
    } else if (claimedUnits !== decoded.amount) {
      problems.push(`the card says another amount; the signed bytes say ${unitsToDecimal(decoded.amount, 6)}`);
    }
  }
  return problems;
}

export function decimalToUnits(decimal: string, places: number): bigint | null {
  if (!/^\d+(\.\d+)?$/.test(decimal)) return null;
  const [w = '0', f = ''] = decimal.split('.');
  if (f.length > places) return null;
  return BigInt(w + f.padEnd(places, '0'));
}

export function unitsToDecimal(units: bigint, places: number): string {
  const s = units.toString().padStart(places + 1, '0');
  const whole = s.slice(0, s.length - places);
  const frac = s.slice(s.length - places).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}

/** Split a txID into readable groups so a human can compare it to a device. */
export function groupTxId(txId: string): string[] {
  return (txId.match(/.{1,8}/g) ?? []).map((g) => g.toLowerCase());
}

/**
 * A text the SERVER chose (a name, a kind) as the page draws it: printable ASCII only, every other code point shown as
 * "·" (Screens and Astra, 27 Oct 2026, round 3: "detect less, control more"). A look-alike letter, an accent, an
 * Arabic-Indic digit, a direction mark — none can be drawn, so what is on screen is what the scan reads. A real name
 * in another script is drawn as dots; the Books show it whole.
 */
export function shownAs(text: string): string {
  // Printable ASCII, and the page's own punctuation (dashes, quotes, ellipsis, dots) — none of which is a letter, a digit
  // or a direction mark, so none can spell or reorder an ending.
  return text.replace(/[^\x20-\x7E—–‘’“”…·•]/gu, '·');
}

/** Every string the server sent, however deep (the direction and look-alike checks read ALL of them — round 6). */
export function serverStrings(v: unknown, out: string[] = []): string[] {
  if (typeof v === 'string') out.push(v);
  else if (v && typeof v === 'object') for (const x of Object.values(v)) serverStrings(x, out);
  return out;
}

/**
 * Whether the names the server chose hold the ending IN ORDER, however it is spaced out — inside one name ("Xcf Y · Pff Q
 * · MRs N", Screens' round 3) or ACROSS them, in whichever order the eye joins them ("Xcf Y Pff Q" + "MRs N", round 5).
 * Measured on the Books' 207 real names (27 Oct 2026): one name, no false hit in 414,000 pairs; a payee's and a desk
 * name joined, 8 in 4.28 million (1.9e-6), forwards or reversed.
 */
export function holdsInOrder(texts: readonly string[], address: string): boolean {
  const tail = address.slice(-CONFIRM_TAIL);
  const runs = [tail, [...tail].reverse().join('')];
  const inOrder = (run: string, s: string) => { let i = 0; for (const c of s) { if (c === run[i]) i++; if (i === run.length) return true; } return false; };
  const orders = (xs: string[]): string[][] => (xs.length <= 1 ? [xs] : xs.flatMap((x, i) => orders([...xs.slice(0, i), ...xs.slice(i + 1)]).map((o) => [x, ...o])));
  const bare = texts.map(skeleton).filter((t) => t.length > 0).slice(0, 5);
  return orders(bare).some((o) => runs.some((run) => inOrder(run, o.join(''))));
}

/**
 * Whether the ending (or its reverse) is up to THREE contiguous pieces, each inside one of the server's texts — any text,
 * any order, one text used again: the eye reads "Xcf Y MRs N" and "Pff Q" as A, B, A (Screens and Astra, round 6). Over
 * the server's own words only — over the page's fixed words it would refuse real cards. Measured on the Books' names
 * (27 Oct 2026): no hit in 200,000 pages.
 */
export function heldInPieces(texts: readonly string[], address: string): boolean {
  const bare = texts.map(skeleton).filter((t) => t.length > 0);
  const has = (p: string) => bare.some((t) => t.includes(p));
  const tail = address.slice(-CONFIRM_TAIL);
  return [tail, [...tail].reverse().join('')].some((run) => {
    for (let i = 1; i < run.length; i++) {
      if (!has(run.slice(0, i))) continue;
      if (has(run.slice(i))) return true;
      for (let j = i + 1; j < run.length; j++) if (has(run.slice(i, j)) && has(run.slice(j))) return true;
    }
    return has(run);
  });
}

/**
 * The writes after the paint (round 7): #status and Sign's label, each checked with the painted words and the OTHER as
 * it stands, while the ending is masked and not typed — what to draw is returned; the page sets it. Pure, so the pair
 * is tested without a card (Screens on 586782f0).
 */
export function afterPaint(masked: boolean, painted: readonly string[], address: string, label: string) {
  let status: string[] = [], typed = false;
  const shows = (s: readonly string[], l: string) => masked && !typed && showsEnding([...painted, ...s, l], address);
  // The fallback is read too, and is nothing when it finishes the ending as well (Astra on the whole page, 3 Nov 2026: a
  // painted "LTRo" and the fallback's "No" made an ending …LTRoNo).
  const pick = (s: string[], fallback: string) => (!shows(s, label) ? s : shows([fallback], label) ? [''] : [fallback]);
  return {
    typed(): void { typed = true; },
    say(text: string, fallback: string): string { status = pick([shownAs(text)], fallback); return status[0]!; },
    fail(text: string, remedy: string | null, fallback: string): string[] {
      return status = pick(remedy === null ? [shownAs(text)] : [shownAs(text), shownAs(remedy)], fallback);
    },
    label(text: string): string { label = !shows(status, text) ? text : shows(status, 'Stopped') ? '' : 'Stopped'; return label; },
  };
}

/**
 * What to compare on the device, behind "What to compare" — by network, with From as the device writes it (after
 * "P<n> -" only for a non-default permission). Plain words and markup; every value escaped by the caller's `esc`.
 */
export function compareSteps(network: Network, permissionId: number, contract: string, esc: (s: unknown) => string): string {
  const prefix = permissionId > 0 ? `, which the device shows after “P${esc(permissionId)} -”` : '';
  return network === 'nile'
    ? `<p class="muted"><b>On the test network your Ledger cannot show the amount or the
      recipient.</b> It does not know test USDT, so it warns <b>Custom Contract</b> and shows the call instead:
      check that <b>Contract</b> is <code class="wrap">${esc(contract)}</code> and <b>From</b>${prefix} is
      the From address above. Pay Token “-” and Call Amount “0” are expected.
      Enough for a test — for real money the device shows the amount and the recipient.</p>`
    : `<p class="muted">Before you approve on the device, compare its screen with this page: the
      <b>Amount</b>, the <b>Token</b> (USDT), <b>To</b> — the recipient address, every character — and
      <b>From</b>${prefix}. <b>Max Fee</b> is the fee limit.</p>`;
}

/** The text nodes a piece of this page's HTML draws, entities decoded — what the eye reads, and so what is scanned. */
export function drawnTexts(html: string): string[] {
  return html.split(/<[^>]*>/)
    .map((t) => t.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&').trim())
    .filter((t) => t.length > 0);
}
